import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { authApi } from "@/lib/crmApi";

const AuthContext = createContext({
  user: null,
  loading: true,
  login: async () => {},
  logout: async () => {},
  refresh: async () => {},
  selectProgramme: async () => {},
});

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const me = await authApi.me();
      // /auth/me returns the merged user + role + allowed_programmes +
      // current_programme. We keep the whole payload in `user` so the rest of
      // the app reads everything from a single state.
      setUser(me);
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    // Cross-context refresh hook. Anything in the app can re-fetch the
    // user payload (e.g. after writing the current_projet cookie from
    // SettingsContext) by dispatching:
    //   window.dispatchEvent(new CustomEvent('crm:auth-refresh'))
    // Avoids tight coupling between contexts while still keeping the
    // session indicator + inbox scope in sync on a project switch.
    const handler = () => { refresh(); };
    window.addEventListener("crm:auth-refresh", handler);
    return () => window.removeEventListener("crm:auth-refresh", handler);
  }, [refresh]);

  const login = async (email, password) => {
    const data = await authApi.login(email, password);
    // After login, fetch the full /me payload so we know about programmes.
    await refresh();
    return data;
  };

  const logout = async () => {
    try {
      await authApi.logout();
    } catch (e) {
      // ignore — clear local state anyway
    }
    setUser(null);
  };

  /**
   * Switch the active programme. The backend persists the choice in a cookie
   * and returns the new value. We refresh the local user payload so any
   * component that reads `user.current_programme` updates immediately.
   *
   * Pass an empty string to clear the selection (admin-only "all programmes").
   */
  const selectProgramme = async (programme) => {
    await authApi.selectProgramme(programme || "");
    await refresh();
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, refresh, selectProgramme }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
